import { Bar } from "react-chartjs-2";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  LinearScale,
  Tooltip,
  type ChartOptions
} from "chart.js";
import { useEffect, useId, useMemo, useState, type ReactNode } from "react";
import { chartValueLabelsPlugin, withValueLabels } from "../../components/chartValueLabels";
import { DataTable } from "../../components/DataTable";
import { ExportButton } from "../../components/ExportButton";
import { FilterBar } from "../../components/FilterBar";
import { PermissionGate } from "../../components/PermissionGate";
import { StatusMessage } from "../../components/StatusMessage";
import { apiGet } from "../../services/api";
import { useAuth } from "../../services/auth";
import type { ProvaFacilFilters, ProvaFacilSnapshot, RankedItem } from "../../types/provaFacil";
import {
  EMPTY_FILTERS,
  averageDuration,
  bottom,
  buildOptions,
  buildSummary,
  formatNumber,
  formatPercent,
  formatShortDate,
  top,
  weekdayLabel
} from "./provaFacilModel";
import "./provaFacil.css";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, chartValueLabelsPlugin);

const filterLabels: Record<keyof ProvaFacilFilters, string> = {
  tipo: "Tipo de avaliação",
  polo: "Polo",
  period: "Período",
  course: "Curso"
};

const horizontalBarOptions: ChartOptions<"bar"> = {
  responsive: true,
  maintainAspectRatio: false,
  indexAxis: "y",
  layout: { padding: { right: 88 } },
  animation: false,
  scales: {
    x: { display: false, beginAtZero: true, grid: { display: false }, border: { display: false } },
    y: {
      grid: { display: false },
      border: { display: false },
      ticks: { color: "#475569", font: { size: 11, weight: 600 } }
    }
  },
  plugins: {
    legend: { display: false },
    tooltip: {
      enabled: true,
      displayColors: false,
      callbacks: {
        label: (context) => {
          const labels = (context.dataset as typeof context.dataset & { valueLabels?: string[] }).valueLabels;
          return labels?.[context.dataIndex] || String(context.formattedValue);
        }
      }
    }
  }
};

type RankingRow = Record<string, string | number | null | undefined> & {
  label: string;
  value: number;
  share: string;
};

type MetricCardProps = {
  label: string;
  value: string;
  detail: string;
  help: string;
  tone: "blue" | "green" | "yellow" | "purple";
};

export function ProvaFacilPage() {
  const { token } = useAuth();
  const [snapshot, setSnapshot] = useState<ProvaFacilSnapshot | null>(null);
  const [filters, setFilters] = useState<ProvaFacilFilters>(EMPTY_FILTERS);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(Boolean(token));

  useEffect(() => {
    if (!token) return;
    let active = true;
    setLoading(true);
    setError("");
    void apiGet<ProvaFacilSnapshot>("/api/prova-facil/dashboard", token)
      .then((payload) => {
        if (active) setSnapshot(payload);
      })
      .catch((err: unknown) => {
        if (active) setError(err instanceof Error ? err.message : "Falha ao carregar Prova Fácil.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [token]);

  const options = useMemo(() => snapshot ? buildOptions(snapshot) : {
    tipo: [], polo: [], period: [], course: []
  }, [snapshot]);
  const summary = useMemo(() => snapshot ? buildSummary(snapshot, filters) : null, [filters, snapshot]);

  const topPolos = useMemo<RankingRow[]>(() => {
    if (!summary) return [];
    return toRankingRows(top(summary.poloCounts), summary.total);
  }, [summary]);

  const lowPolos = useMemo<RankingRow[]>(() => {
    if (!summary) return [];
    return toRankingRows(bottom(summary.poloCounts), summary.total);
  }, [summary]);

  const activeFilterCount = Object.values(filters).filter(Boolean).length;
  const generatedShare = summary?.total ? (summary.generatedTotal / summary.total) * 100 : 0;

  function updateFilter(key: keyof ProvaFacilFilters, value: string) {
    setFilters((current) => ({ ...current, [key]: value }));
  }

  return (
    <PermissionGate permission="prova-facil">
      <div className="prova-facil-page">
        <header className="pf-header">
          <div>
            <p className="eyebrow">Operação de avaliações</p>
            <h1>Prova Fácil</h1>
            <p>Distribuição de provas, presença e capacidade operacional no recorte selecionado.</p>
          </div>
          {snapshot ? <UpdatedBadge generatedAt={snapshot.generatedAt} /> : null}
        </header>

        {loading ? <StatusMessage>Carregando dados analíticos...</StatusMessage> : null}
        {error ? <StatusMessage tone="danger" title="Não foi possível carregar o painel">{error}</StatusMessage> : null}

        {snapshot && summary ? (
          <>
            <section className="pf-toolbar" aria-label="Controles do painel">
              <div className="pf-toolbar__summary">
                <span>{formatNumber(summary.total)} provas no recorte</span>
                <span>{activeFilterCount ? `${activeFilterCount} filtro${activeFilterCount === 1 ? "" : "s"} ativo${activeFilterCount === 1 ? "" : "s"}` : "Visão geral"}</span>
              </div>
              <div className="toolbar-row">
                <FilterBar
                  filters={filters}
                  options={options}
                  labels={filterLabels}
                  onChange={updateFilter}
                  onReset={() => setFilters(EMPTY_FILTERS)}
                />
                <ExportButton filename="prova-facil-snapshot.json" payload={{ filters, summary }} />
              </div>
            </section>

            <section className="pf-kpi-grid" aria-label="Indicadores principais">
              <MetricCard label="Provas no recorte" value={formatNumber(summary.total)} detail={`${formatShortDate(summary.range.min)} a ${formatShortDate(summary.range.max)}`} help="Cálculo: soma das provas que atendem aos filtros, usando somente os tipos 001, 002, 003 e 004. Este indicador é uma contagem e não possui denominador." tone="blue" />
              <MetricCard label="Alunos presentes únicos" value={formatNumber(summary.presentUniqueCount)} detail="RA único por dia de prova" help="Cálculo: cada combinação de RA e dia da prova é contada uma vez. O indicador é uma contagem, não uma taxa de presença, e não possui denominador." tone="green" />
              <MetricCard label="Provas geradas" value={formatNumber(summary.generatedTotal)} detail={`${formatPercent(generatedShare)} das provas no recorte`} help="Cálculo: provas com status que contém “Gerado” ÷ total de provas do recorte × 100. O total do recorte é o denominador." tone="yellow" />
              <MetricCard label="Duração média" value={summary.durationCount ? `${averageDuration(summary).toFixed(0)} min` : "—"} detail={`${formatNumber(summary.durationCount)} registros com duração`} help="Cálculo: soma das durações em minutos ÷ quantidade de provas com duração válida. Registros sem duração não entram no denominador." tone="purple" />
            </section>

            {summary.total ? (
              <>
                <section className="pf-analysis-grid" aria-label="Análises gráficas">
                  <AnalysisPanel title="Tipos de avaliação" subtitle="Volume e participação no recorte" help="Cada barra soma as provas do tipo indicado. O percentual é o valor da barra dividido pelo total de provas do recorte. São considerados somente os tipos 001 a 004.">
                    <RankedBarChart items={top(summary.tipoCounts, 6).map((item) => ({ ...item, label: item.label.replace(/^\d+\s*-\s*/, "") }))} total={summary.total} color="#1d446c" />
                  </AnalysisPanel>
                  <AnalysisPanel title="Horários de início" subtitle="Horários com provas, em ordem cronológica" help="Cada barra soma as provas iniciadas naquela hora. O percentual usa como denominador somente as provas que possuem horário de início registrado no recorte.">
                    <HourlyBarChart values={summary.hours} />
                  </AnalysisPanel>
                  <AnalysisPanel title="Dias da semana" subtitle="Volume e participação semanal" help="Cada barra soma as provas pela data registrada. O percentual é o valor do dia dividido pelo total de provas do recorte.">
                    <MetricBars items={summary.weekdays.map((value, index) => ({ label: weekdayLabel(index), value }))} />
                  </AnalysisPanel>
                  <AnalysisPanel title="Cursos com mais provas" subtitle="Oito maiores volumes no recorte" help="Os cursos são ordenados do maior para o menor volume e o painel mostra os oito primeiros. O percentual divide as provas do curso pelo total de provas do recorte.">
                    <RankedBarChart items={top(summary.courseCounts)} total={summary.total} color="#0f766e" />
                  </AnalysisPanel>
                </section>

                <section className="pf-ranking-grid" aria-label="Rankings de polos">
                  <AnalysisPanel title="Polos com mais provas" subtitle="Maiores volumes e participação no recorte" help="Os polos são ordenados do maior para o menor volume. A participação divide as provas do polo pelo total de provas do recorte.">
                    <RankingTable rows={topPolos} />
                  </AnalysisPanel>
                  <AnalysisPanel title="Polos com menos provas" subtitle="Menores volumes positivos no recorte" help="Mostra os oito menores volumes acima de zero. A participação divide as provas do polo pelo total de provas do recorte.">
                    <RankingTable rows={lowPolos} />
                  </AnalysisPanel>
                </section>
              </>
            ) : (
              <StatusMessage tone="warning" title="Nenhum resultado">
                Não existem provas para a combinação selecionada. Limpe ou ajuste os filtros para ampliar o recorte.
              </StatusMessage>
            )}
          </>
        ) : !loading && !error ? (
          <StatusMessage tone="warning" title="Snapshot indisponível">
            O processamento de Prova Fácil ainda não publicou dados para este ambiente.
          </StatusMessage>
        ) : null}
      </div>
    </PermissionGate>
  );
}

function MetricCard({ label, value, detail, help, tone }: MetricCardProps) {
  const tooltipId = useId();
  return (
    <article className={`pf-kpi pf-kpi--${tone}`}>
      <div className="pf-kpi__label">
        <span>{label}</span>
        <button type="button" className="pf-help" aria-label={`Como é calculado: ${label}`} aria-describedby={tooltipId}>i<span id={tooltipId} role="tooltip">{help}</span></button>
      </div>
      <strong>{value}</strong>
      <small>{detail}</small>
    </article>
  );
}

function AnalysisPanel({ title, subtitle, help, children }: { title: string; subtitle: string; help: string; children: ReactNode }) {
  const tooltipId = useId();
  return (
    <section className="pf-panel">
      <header className="pf-panel__head">
        <div><h2>{title}</h2><p>{subtitle}</p></div>
        <button type="button" className="pf-panel-help" aria-label={`Como esta análise é calculada: ${title}`} aria-describedby={tooltipId}>
          i
          <span id={tooltipId} role="tooltip">{help}</span>
        </button>
      </header>
      <div className="pf-panel__body">{children}</div>
    </section>
  );
}

function RankedBarChart({ items, total, color }: { items: RankedItem[]; total: number; color: string }) {
  if (!items.length) return <p className="pf-empty">Sem dados no recorte atual.</p>;
  const values = items.map((item) => item.value);
  return (
    <div className="pf-chart pf-chart--ranked">
      <Bar
        data={{
          labels: items.map((item) => item.label),
          datasets: [withValueLabels({ data: values, backgroundColor: color, borderRadius: 5, barThickness: 18 }, values.map((value) => valueAndShare(value, total)))]
        }}
        options={horizontalBarOptions}
        role="img"
        aria-label="Gráfico de barras com valores e percentuais"
      />
    </div>
  );
}

function HourlyBarChart({ values }: { values: number[] }) {
  const items = values
    .map((value, hour) => ({ label: `${String(hour).padStart(2, "0")}h`, value }))
    .filter((item) => item.value > 0);
  const total = items.reduce((sum, item) => sum + item.value, 0);
  if (!items.length) return <p className="pf-empty">Sem horários de início no recorte atual.</p>;
  return (
    <div className="pf-chart pf-chart--hours" style={{ blockSize: `${Math.max(270, items.length * 29)}px` }}>
      <Bar
        data={{
          labels: items.map((item) => item.label),
          datasets: [withValueLabels({ data: items.map((item) => item.value), backgroundColor: "#d2102e", borderRadius: 4, barThickness: 17 }, items.map((item) => valueAndShare(item.value, total)))]
        }}
        options={horizontalBarOptions}
        role="img"
        aria-label="Distribuição das provas por horário de início, com valores e percentuais"
      />
    </div>
  );
}

function MetricBars({ items }: { items: RankedItem[] }) {
  const total = items.reduce((sum, item) => sum + item.value, 0);
  const max = Math.max(...items.map((item) => item.value), 1);
  return (
    <div className="pf-bars" role="img" aria-label="Distribuição por dia da semana">
      {items.map((item) => (
        <div className="pf-bar" key={item.label}>
          <div className="pf-bar__meta"><span>{item.label}</span><strong>{valueAndShare(item.value, total)}</strong></div>
          <div className="pf-bar__track" aria-hidden="true"><span style={{ inlineSize: `${(item.value / max) * 100}%` }} /></div>
        </div>
      ))}
    </div>
  );
}

function RankingTable({ rows }: { rows: RankingRow[] }) {
  return <DataTable columns={[{ key: "label", label: "Polo" }, { key: "value", label: "Provas" }, { key: "share", label: "% do recorte" }]} rows={rows} />;
}

function UpdatedBadge({ generatedAt }: { generatedAt: string }) {
  const formatted = formatDateTime(generatedAt);
  return <div className="pf-updated" aria-label={`Snapshot atualizado em ${formatted}`}><span aria-hidden="true" /><div><small>Dados atualizados</small><strong>{formatted}</strong></div></div>;
}

function toRankingRows(items: RankedItem[], total: number): RankingRow[] {
  return items.map((item) => ({ label: item.label, value: item.value, share: total ? formatPercent((item.value / total) * 100) : "0%" }));
}

function valueAndShare(value: number, total: number): string {
  return `${formatNumber(value)} · ${formatPercent(total ? (value / total) * 100 : 0)}`;
}

function formatDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value || "Não informado";
  return date.toLocaleString("pt-BR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}
